import { FoodItem, FoodItemInputView } from "@corpuscomplete/web/components/nutrition/food-item";
import { FoodSearchCombobox } from "@corpuscomplete/web/components/nutrition/food-search-combobox";
import { FoodSearchDialog } from "@corpuscomplete/web/components/nutrition/food-search-dialog";
import {
  VerticalDragReorderOverlay,
  VerticalDragReorderShellRow,
} from "@corpuscomplete/web/components/common/vertical-drag-reorder";
import { useVerticalDragReorder } from "@corpuscomplete/web/hooks/use-vertical-drag-reorder";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { ChevronDown, Folder, X } from "lucide-react";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { withFieldGroup, withForm } from "@corpuscomplete/web/hooks/create-form-hook";
import { Recipe } from "@corpuscomplete/core/nutrition/recipe";
import { RecipeBase } from "@corpuscomplete/core/nutrition/recipe-base";
import { Food } from "@corpuscomplete/core/nutrition/food";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { useStore } from "@tanstack/react-form";
import { NutrientUI } from "@corpuscomplete/core-ui/nutrition/nutrient";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { FoodUI } from "@corpuscomplete/core-ui/nutrition/food";
import { FoodSearchUI } from "@corpuscomplete/core-ui/nutrition/food/search";
import { FoodCollectionUI } from "@corpuscomplete/core-ui/nutrition/food-collection";
import { RecipeMetric, RecipePageSection, RecipeStepNumber } from "./recipe-page-section";
import { useMemo, useState } from "react";

export const RecipeFormComponent = withForm({
  defaultValues: Recipe.formDefault(undefined),
  validators: { onSubmit: Recipe.formSchema("validation") },
  render: ({ form }) => {
    const allCollections = FoodCollectionUI.useForContentManagement({ foodType: "recipe" });
    const variationCount = useStore(form.store, (state) => state.values.variations.length);
    const ingredientInputs = useStore(form.store, (state) => state.values.ingredients);
    const selectedCollectionId = useStore(form.store, (state) => state.values.meta.collectionId);
    const [activeVariationIndex, setActiveVariationIndex] = useState(0);
    const safeIndex =
      activeVariationIndex >= 0 && activeVariationIndex < variationCount ? activeVariationIndex : 0;

    const ingredientItems = FoodUI.useInputsAsItems(ingredientInputs);
    const selectedCollection = allCollections?.find(
      (collection) => collection.id === selectedCollectionId,
    );
    const collections = selectedCollection?.scopeId
      ? allCollections.filter((collection) => collection.scopeId === selectedCollection.scopeId)
      : allCollections;

    const { nutrients } = NutrientUI.useDisplayNutrients();
    const nutrientAmounts = Nutrient.amountsToInfo(
      Nutrient.addFoodItems(Nutrient.emptyAmounts(), ingredientItems),
      nutrients,
    );

    const VariationForm = ({ variationIndex }: { variationIndex: number }) => {
      // TODO: Some sort of RecipeVariation context to pull the base recipe here to calculate applyPatch errors
      const variationIngredientInputs = useStore(
        form.store,
        (state) => state.values.variations[variationIndex]?.ingredients ?? [],
      );
      const variationIngredients = FoodUI.useInputsAsItems(variationIngredientInputs);
      const variationNutrientAmounts = Nutrient.amountsToInfo(
        Nutrient.addFoodItems(Nutrient.emptyAmounts(), variationIngredients),
        nutrients,
      );

      return (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Text variant="h4">Recipe Details</Text>
            <div className="flex flex-row gap-2">
              <form.AppField name={`variations[${variationIndex}].base.name`}>
                {(field) => (
                  <field.Input label="Name" placeholder="Name" className="flex-1" autoFocus />
                )}
              </form.AppField>
              <form.AppField name={`variations[${variationIndex}].recipe.prepTimeMinutes`}>
                {(field) => <field.Input label="Prep Time" placeholder="15 mins" />}
              </form.AppField>
              <form.AppField name={`variations[${variationIndex}].recipe.cookTimeMinutes`}>
                {(field) => <field.Input label="Cook Time" placeholder="30 mins" />}
              </form.AppField>
            </div>

            <form.AppField name={`variations[${variationIndex}].recipe.description`}>
              {(field) => <field.Textarea label="Description" placeholder="Description" />}
            </form.AppField>
          </div>

          <div className="flex flex-row gap-2">
            <div className="flex flex-grow-[3] flex-col gap-2" style={{ flexBasis: 0 }}>
              <Text variant="h4">Ingredients</Text>
              <form.AppField name={`variations[${variationIndex}].ingredients`} mode="array">
                {(field) => (
                  <IngredientEditor
                    ingredients={field.state.value}
                    onAddIngredient={(foodServingId) => {
                      if (
                        field.state.value.some(
                          (ingredient) => ingredient.foodServingId === foodServingId,
                        )
                      ) {
                        return;
                      }

                      field.pushValue({ amount: 1, foodServingId });
                    }}
                    onEditIngredientAmount={(foodServingId, amount) => {
                      const index = field.state.value.findIndex(
                        (ingredient) => ingredient.foodServingId === foodServingId,
                      );
                      const ingredient = field.state.value[index];
                      if (!ingredient) {
                        return;
                      }

                      field.replaceValue(index, { ...ingredient, amount });
                    }}
                    onEditIngredientServing={(foodServingId, serving) => {
                      const index = field.state.value.findIndex(
                        (ingredient) => ingredient.foodServingId === foodServingId,
                      );
                      const ingredient = field.state.value[index];
                      if (!ingredient) {
                        return;
                      }

                      field.replaceValue(index, {
                        ...ingredient,
                        foodServingId: serving.id,
                        amount: serving.defaultAmount,
                      });
                    }}
                    onRemoveIngredient={(foodServingId) => {
                      const index = field.state.value.findIndex(
                        (ingredient) => ingredient.foodServingId === foodServingId,
                      );
                      if (index !== -1) {
                        field.removeValue(index);
                      }
                    }}
                    onReorderIngredients={(ingredients) => field.setValue(() => [...ingredients])}
                    scrollClassName="max-h-64 overflow-y-auto"
                  />
                )}
              </form.AppField>
            </div>

            <div className="flex flex-grow-[2] flex-col gap-4" style={{ flexBasis: 0 }}>
              {/* TODO: Add serving stuff from base recipe */}
              {/* <div className="flex flex-col gap-2">
                <Text variant="h4">Yields</Text>
                <div className="flex flex-row gap-2">
                  <form.AppField name={`variations[${variationIndex}].serving.perRecipe`}>
                    {(field) => <field.Input placeholder="4" />}
                  </form.AppField>
                  <form.AppField name={`variations[${variationIndex}].serving.name`}>
                    {(field) => <field.Input placeholder="serving(s)" />}
                  </form.AppField>
                </div>
              </div> */}
              <Text variant="h4">Nutrition Per Serving</Text>
              <div className="flex flex-col">
                {variationNutrientAmounts.map(({ nutrient, amount }) => (
                  <Text key={nutrient.id} variant="sm">
                    {nutrient.defaultName}: {amount.toFixed(1)}
                    {nutrient.unit?.defaultName}
                  </Text>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Text variant="h4">Instructions</Text>
            <form.AppField name={`variations[${variationIndex}].steps`} mode="array">
              {(field) => (
                <>
                  {field.state.value.map((_step, i) => (
                    <div key={i} className="flex flex-row gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                        <Text variant="sm" className="text-primary-foreground">
                          {i + 1}
                        </Text>
                      </div>
                      <form.AppField name={`variations[${variationIndex}].steps[${i}].text`}>
                        {(subField) => (
                          <subField.Textarea
                            className="flex-1"
                            placeholder={`Step ${i + 1} instructions`}
                          />
                        )}
                      </form.AppField>
                      {field.state.value.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => field.removeValue(i)}>
                          <X className="size-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  <Button variant="ghost" onClick={() => field.pushValue({ text: "" })}>
                    <Text variant="sm">Add Step</Text>
                  </Button>
                </>
              )}
            </form.AppField>
          </div>
        </div>
      );
    };

    return (
      <>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <form.AppField name="base.name">
              {(field) => (
                <field.Input
                  placeholder="Untitled recipe"
                  autoFocus
                  variant="ghost"
                  textVariant="h1"
                  inputClassName="h-auto px-0 text-balance"
                />
              )}
            </form.AppField>
            <form.AppField name="recipe.description">
              {(field) => (
                <field.Textarea
                  placeholder="Add a quick description that sets expectations for the recipe."
                  variant="ghost"
                  textVariant="md"
                  textareaClassName="min-h-0 px-0 leading-6 text-muted-foreground"
                />
              )}
            </form.AppField>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <form.AppField name="meta.collectionId">
                {(field) => (
                  <div className="flex flex-col gap-1">
                    <Label>Collection</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            variant="ghost"
                            className="h-auto justify-between px-0 py-0 text-left"
                          />
                        }
                      >
                        <div className="flex items-center gap-2">
                          <Folder className="size-4 text-muted-foreground" />
                          <Text variant="sm">
                            {selectedCollection?.name ?? "Select a collection"}
                          </Text>
                        </div>
                        <ChevronDown className="size-4" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {collections?.map((collection) => (
                          <DropdownMenuItem
                            key={collection.id}
                            onClick={() => field.handleChange(collection.id)}
                          >
                            <Text variant="sm">{collection.name}</Text>
                          </DropdownMenuItem>
                        ))}
                        {!collections?.length && (
                          <DropdownMenuItem disabled>
                            <Text variant="sm" className="text-muted-foreground">
                              No collections available
                            </Text>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {field.state.meta.errors.length > 0 && (
                      <Text variant="sm" className="text-destructive">
                        {field.state.meta.errors.map((error) => error?.message).join(", ")}
                      </Text>
                    )}
                  </div>
                )}
              </form.AppField>
              <form.AppField name="recipe.prepTimeMinutes">
                {(field) => <field.Input label="Prep Time" placeholder="15" endText="mins" />}
              </form.AppField>
              <form.AppField name="recipe.cookTimeMinutes">
                {(field) => <field.Input label="Cook Time" placeholder="30" endText="mins" />}
              </form.AppField>
              <RecipeMetric
                label="Variations"
                value={
                  variationCount === 0 ? "Base recipe only" : `${variationCount + 1} versions total`
                }
              />
            </div>
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(18rem,1fr)]">
            <RecipePageSection
              title="Ingredients"
              description="Search and add the foods that make up this recipe."
            >
              <form.AppField name="ingredients" mode="array">
                {(field) => (
                  <IngredientEditor
                    ingredients={field.state.value}
                    onAddIngredient={(foodServingId) => {
                      if (
                        field.state.value.some(
                          (ingredient) => ingredient.foodServingId === foodServingId,
                        )
                      ) {
                        return;
                      }

                      field.pushValue({ amount: 1, foodServingId });
                    }}
                    onEditIngredientAmount={(foodServingId, amount) => {
                      const index = field.state.value.findIndex(
                        (ingredient) => ingredient.foodServingId === foodServingId,
                      );
                      const ingredient = field.state.value[index];
                      if (!ingredient) {
                        return;
                      }

                      field.replaceValue(index, { ...ingredient, amount });
                    }}
                    onEditIngredientServing={(foodServingId, serving) => {
                      const index = field.state.value.findIndex(
                        (ingredient) => ingredient.foodServingId === foodServingId,
                      );
                      const ingredient = field.state.value[index];
                      if (!ingredient) {
                        return;
                      }

                      field.replaceValue(index, {
                        ...ingredient,
                        foodServingId: serving.id,
                        amount: serving.defaultAmount,
                      });
                    }}
                    onRemoveIngredient={(foodServingId) => {
                      const index = field.state.value.findIndex(
                        (ingredient) => ingredient.foodServingId === foodServingId,
                      );
                      if (index !== -1) {
                        field.removeValue(index);
                      }
                    }}
                    onReorderIngredients={(ingredients) => field.setValue(() => [...ingredients])}
                    errors={field.state.meta.errors.flatMap((error) =>
                      error?.message ? [error.message] : [],
                    )}
                  />
                )}
              </form.AppField>
            </RecipePageSection>

            <div className="flex flex-col gap-6">
              <RecipePageSection title="Yield" description="How much the full recipe makes.">
                <div className="grid gap-3 sm:grid-cols-2">
                  <form.AppField name="serving.perRecipe">
                    {(field) => <field.Input label="Amount" placeholder="4" />}
                  </form.AppField>
                  <form.AppField name="serving.name">
                    {(field) => <field.Input label="Unit" placeholder="serving(s)" />}
                  </form.AppField>
                </div>
              </RecipePageSection>

              <RecipePageSection
                title="Nutrition Per Serving"
                description="Calculated automatically from the current ingredients."
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  {nutrientAmounts.map(({ nutrient, amount }) => (
                    <RecipeMetric
                      key={nutrient.id}
                      label={nutrient.defaultName}
                      value={`${amount.toFixed(1)}${nutrient.unit?.defaultName ?? ""}`}
                    />
                  ))}
                </div>
              </RecipePageSection>
            </div>
          </div>

          <RecipePageSection title="Instructions" description="Write the cooking steps in order.">
            <form.AppField name="steps" mode="array">
              {(field) => (
                <div className="flex flex-col gap-3">
                  {field.state.value.map((_step, i) => (
                    <div key={i} className="flex gap-3">
                      <RecipeStepNumber>{i + 1}</RecipeStepNumber>
                      <form.AppField name={`steps[${i}].text`}>
                        {(subField) => (
                          <subField.Textarea
                            className="flex-1"
                            textareaClassName="min-h-20 bg-background"
                            placeholder={`Step ${i + 1} instructions`}
                          />
                        )}
                      </form.AppField>
                      {field.state.value.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="mt-6"
                          onClick={() => field.removeValue(i)}
                        >
                          <X className="size-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  <Button
                    variant="ghost"
                    className="w-fit"
                    onClick={() => field.pushValue({ text: "" })}
                  >
                    <Text variant="sm">Add Step</Text>
                  </Button>
                </div>
              )}
            </form.AppField>
          </RecipePageSection>
        </div>

        <form.AppField name="variations" mode="array">
          {(field) => (
            <RecipePageSection
              title="Curated Variations"
              description="Author snapshot-based alternatives such as high-protein or vegetarian versions."
            >
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => field.pushValue({ ...RecipeBase.formDefault(), originalId: null })}
                >
                  Add Variation
                </Button>
              </div>

              {field.state.value.length === 0 && (
                <Text variant="sm" className="text-muted-foreground">
                  Add curated variations to author snapshot-based recipe alternatives.
                </Text>
              )}

              <div className="flex flex-wrap gap-2">
                {field.state.value.map((variation, i) => (
                  <Button
                    key={i}
                    size="sm"
                    variant={activeVariationIndex === i ? "default" : "outline"}
                    onClick={() => setActiveVariationIndex(i)}
                  >
                    {variation.base.name || "Untitled"}
                  </Button>
                ))}
              </div>
            </RecipePageSection>
          )}
        </form.AppField>
        {variationCount > 0 && <VariationForm variationIndex={safeIndex} />}
      </>
    );
  },
});

type IngredientInput = RecipeBase.Form["ingredients"][number];

function IngredientEditor({
  ingredients,
  onAddIngredient,
  onEditIngredientAmount,
  onEditIngredientServing,
  onRemoveIngredient,
  onReorderIngredients,
  scrollClassName,
  errors = [],
}: {
  ingredients: readonly IngredientInput[];
  onAddIngredient: (foodServingId: string) => void;
  onEditIngredientAmount: (foodServingId: string, amount: number) => void;
  onEditIngredientServing: (foodServingId: string, serving: Food.Serving) => void;
  onRemoveIngredient: (foodServingId: string) => void;
  onReorderIngredients: (ingredients: readonly IngredientInput[]) => void;
  scrollClassName?: string;
  errors?: readonly string[];
}) {
  const reorder = useVerticalDragReorder({
    items: ingredients,
    getItemId: (ingredient) => ingredient.foodServingId,
    getItemSignature: (ingredient) => `${ingredient.amount}`,
    onReorder: ({ projectedItems }) => {
      onReorderIngredients(projectedItems);
    },
  });
  const searchState = FoodSearchUI.useSearchState();
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState<Food.Item[]>([]);
  const existingServingIds = useMemo(
    () => new Set(ingredients.map((ingredient) => ingredient.foodServingId)),
    [ingredients],
  );
  const pendingServingIds = useMemo(
    () => new Set(pendingItems.map((item) => item.serving.id)),
    [pendingItems],
  );

  const closeAdvancedSearch = () => {
    setAdvancedSearchOpen(false);
    setPendingItems([]);
    searchState.reset();
  };

  const onSelectPendingItem = (item: Food.Item) => {
    if (existingServingIds.has(item.serving.id) || pendingServingIds.has(item.serving.id)) {
      return;
    }

    setPendingItems((currentItems) => [...currentItems, item]);
  };

  const onAddPendingIngredients = () => {
    for (const item of pendingItems) {
      onAddIngredient(item.serving.id);
    }

    closeAdvancedSearch();
  };

  return (
    <div className="flex flex-col gap-2">
      <FoodSearchCombobox
        placeholder="Search foods to add as ingredients"
        onSelect={(item) => onAddIngredient(item.serving.id)}
        onOpenAdvancedSearch={({ text }) => {
          searchState.onChangeText(text);
          setAdvancedSearchOpen(true);
        }}
      />
      <FoodSearchDialog
        open={advancedSearchOpen}
        onOpenChange={(open) => {
          if (open) {
            setAdvancedSearchOpen(true);
            return;
          }

          closeAdvancedSearch();
        }}
        searchState={searchState}
        onSelectItem={onSelectPendingItem}
        title="Add recipe ingredients"
        description="Search foods and select ingredients to add to this recipe."
        placeholder="Search foods to add as ingredients"
        renderSidePanel={() => (
          <PendingIngredientsPanel
            pendingItems={pendingItems}
            onRemovePendingItem={(foodServingId) =>
              setPendingItems((currentItems) =>
                currentItems.filter((item) => item.serving.id !== foodServingId),
              )
            }
          />
        )}
        renderFooter={() => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={closeAdvancedSearch}>
              <Text variant="sm">Cancel</Text>
            </Button>
            <Button
              size="sm"
              disabled={pendingItems.length === 0}
              onClick={onAddPendingIngredients}
            >
              <Text variant="sm">
                {pendingItems.length === 1
                  ? "Add 1 ingredient"
                  : `Add ${pendingItems.length} ingredients`}
              </Text>
            </Button>
          </div>
        )}
      />

      <DndContext {...reorder.dndContextProps}>
        <div className={scrollClassName}>
          <div className="flex flex-col gap-2">
            {ingredients.map((ingredient) => {
              return (
                <VerticalDragReorderShellRow
                  key={ingredient.foodServingId}
                  className="cursor-default"
                  {...reorder.getRowProps(ingredient.foodServingId)}
                >
                  <FoodItemInputView
                    itemInput={ingredient}
                    onEditItemAmount={(amount) =>
                      onEditIngredientAmount(ingredient.foodServingId, amount)
                    }
                    onEditItemServing={(serving) =>
                      onEditIngredientServing(ingredient.foodServingId, serving)
                    }
                    onRemoveItem={() => onRemoveIngredient(ingredient.foodServingId)}
                  />
                </VerticalDragReorderShellRow>
              );
            })}
          </div>
        </div>
        <DragOverlay dropAnimation={null}>
          <VerticalDragReorderOverlay item={reorder.activeItem} width={reorder.overlayWidth}>
            {(ingredient) => (
              <FoodItemInputView
                itemInput={ingredient}
                onEditItemAmount={() => undefined}
                onEditItemServing={() => undefined}
                onRemoveItem={() => undefined}
              />
            )}
          </VerticalDragReorderOverlay>
        </DragOverlay>
      </DndContext>

      {errors.length > 0 && (
        <Text variant="sm" className="text-destructive">
          {errors.join(", ")}
        </Text>
      )}
    </div>
  );
}

function PendingIngredientsPanel({
  pendingItems,
  onRemovePendingItem,
}: {
  pendingItems: Food.Item[];
  onRemovePendingItem: (foodServingId: string) => void;
}) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center border-b px-1 py-1">
        <Text variant="sm" className="font-semibold">
          Pending ingredients
        </Text>
      </div>
      <div className="flex flex-col overflow-y-auto">
        {pendingItems.length === 0 ? (
          <div className="pt-[8.75rem] text-center">
            <Text variant="sm" className="text-muted-foreground">
              No pending ingredients.
            </Text>
          </div>
        ) : (
          pendingItems.map((item) => (
            <FoodItem
              key={item.serving.id}
              item={item}
              onRemove={() => onRemovePendingItem(item.serving.id)}
              titleLines={2}
            />
          ))
        )}
      </div>
    </div>
  );
}

export const RecipeVariationFormComponent = withFieldGroup({
  defaultValues: RecipeBase.formDefault(),
  render: ({ group }) => {
    // TODO: Some sort of RecipeVariation context to pull the base recipe here to calculate applyPatch errors
    const ingredientInputs = useStore(group.store, (state) => state.values.ingredients);
    const ingredients = FoodUI.useInputsAsItems(ingredientInputs);

    const { nutrients } = NutrientUI.useDisplayNutrients();
    const nutrientAmounts = Nutrient.amountsToInfo(
      Nutrient.addFoodItems(Nutrient.emptyAmounts(), ingredients),
      nutrients,
    );

    return (
      <div className="flex flex-col gap-6 border-l border-dashed pl-4 md:pl-6">
        <div className="flex flex-col gap-4">
          <group.AppField name="base.name">
            {(field) => (
              <field.Input
                placeholder="Untitled variation"
                className="flex-1"
                autoFocus
                variant="ghost"
                textVariant="h3"
                inputClassName="h-auto px-0"
              />
            )}
          </group.AppField>

          <group.AppField name="recipe.description">
            {(field) => (
              <field.Textarea
                placeholder="Describe what changes in this version."
                variant="ghost"
                textVariant="sm"
                textareaClassName="min-h-0 px-0 text-muted-foreground"
              />
            )}
          </group.AppField>

          <div className="grid gap-3 md:grid-cols-2">
            <group.AppField name="recipe.prepTimeMinutes">
              {(field) => <field.Input label="Prep Time" placeholder="15" endText="mins" />}
            </group.AppField>
            <group.AppField name="recipe.cookTimeMinutes">
              {(field) => <field.Input label="Cook Time" placeholder="30" endText="mins" />}
            </group.AppField>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(18rem,1fr)]">
          <RecipePageSection
            title="Ingredients"
            description="Override the ingredient list for this variation."
          >
            <group.AppField name="ingredients" mode="array">
              {(field) => (
                <IngredientEditor
                  ingredients={field.state.value}
                  onAddIngredient={(foodServingId) => {
                    if (
                      field.state.value.some(
                        (ingredient) => ingredient.foodServingId === foodServingId,
                      )
                    ) {
                      return;
                    }

                    field.pushValue({ amount: 1, foodServingId });
                  }}
                  onEditIngredientAmount={(foodServingId, amount) => {
                    const index = field.state.value.findIndex(
                      (ingredient) => ingredient.foodServingId === foodServingId,
                    );
                    const ingredient = field.state.value[index];
                    if (!ingredient) {
                      return;
                    }

                    field.replaceValue(index, { ...ingredient, amount });
                  }}
                  onEditIngredientServing={(foodServingId, serving) => {
                    const index = field.state.value.findIndex(
                      (ingredient) => ingredient.foodServingId === foodServingId,
                    );
                    const ingredient = field.state.value[index];
                    if (!ingredient) {
                      return;
                    }

                    field.replaceValue(index, {
                      ...ingredient,
                      foodServingId: serving.id,
                      amount: serving.defaultAmount,
                    });
                  }}
                  onRemoveIngredient={(foodServingId) => {
                    const index = field.state.value.findIndex(
                      (ingredient) => ingredient.foodServingId === foodServingId,
                    );
                    if (index !== -1) {
                      field.removeValue(index);
                    }
                  }}
                  onReorderIngredients={(ingredients) => field.setValue(() => [...ingredients])}
                />
              )}
            </group.AppField>
          </RecipePageSection>

          <div className="flex flex-col gap-6">
            {/* TODO: Add serving stuff from base recipe */}
            <RecipePageSection
              title="Nutrition Per Serving"
              description="Calculated automatically from this variation's ingredients."
            >
              <div className="grid gap-2 sm:grid-cols-2">
                {nutrientAmounts.map(({ nutrient, amount }) => (
                  <RecipeMetric
                    key={nutrient.id}
                    label={nutrient.defaultName}
                    value={`${amount.toFixed(1)}${nutrient.unit?.defaultName ?? ""}`}
                  />
                ))}
              </div>
            </RecipePageSection>
          </div>
        </div>

        <RecipePageSection
          title="Instructions"
          description="Override the preparation steps for this variation."
        >
          <group.AppField name="steps" mode="array">
            {(field) => (
              <div className="flex flex-col gap-3">
                {field.state.value.map((_step, i) => (
                  <div key={i} className="flex gap-3">
                    <RecipeStepNumber>{i + 1}</RecipeStepNumber>
                    <group.AppField name={`steps[${i}].text`}>
                      {(subField) => (
                        <subField.Textarea
                          className="flex-1"
                          textareaClassName="min-h-20 bg-background"
                          placeholder={`Step ${i + 1} instructions`}
                        />
                      )}
                    </group.AppField>
                    {field.state.value.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mt-6"
                        onClick={() => field.removeValue(i)}
                      >
                        <X className="size-4" />
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  variant="ghost"
                  className="w-fit"
                  onClick={() => field.pushValue({ text: "" })}
                >
                  <Text variant="sm">Add Step</Text>
                </Button>
              </div>
            )}
          </group.AppField>
        </RecipePageSection>
      </div>
    );
  },
});
// TODO: Eventually remove, but there might be some useful parts of this

// function CuratedVariationsField({
//   form,
//   selectedVariationIndex,
//   setSelectedVariationIndex,
// }: {
//   form: RecipeForm.Api;
//   selectedVariationIndex: number | null;
//   setSelectedVariationIndex: (index: number | null) => void;
// }) {
//   return (
//     <form.AppField name="curatedVariations" mode="array">
//       {(field) => (
//         <div className="flex flex-col gap-2">
//           <div className="flex items-center justify-between">
//             <Text variant="h4">Curated Variations</Text>
//             <Button
//               variant="outline"
//               size="sm"
//               onClick={() => RecipeForm.addCuratedVariation(field)}
//             >
//               Add Variation
//             </Button>
//           </div>

//           {field.state.value.length === 0 && (
//             <Text variant="sm" className="text-muted-foreground">
//               Add curated variations to author snapshot-based recipe alternatives.
//             </Text>
//           )}

//           <div className="flex flex-wrap gap-2">
//             {field.state.value.map((variation, i) => (
//               <Button
//                 key={variation.id}
//                 size="sm"
//                 variant={selectedVariationIndex === i ? "default" : "outline"}
//                 onClick={() => setSelectedVariationIndex(i)}
//               >
//                 {variation.label || "Untitled"}
//               </Button>
//             ))}
//           </div>

//           {field.state.value.map((variation, i) => (
//             <div
//               className={cn("rounded-md border p-3", selectedVariationIndex !== i ? "hidden" : "")}
//             >
//               <div className="mb-2 flex items-center justify-between gap-2">
//                 <form.AppField name={`curatedVariations[${i}].label`}>
//                   {(subField) => (
//                     <subField.Input
//                       label="Variation Label"
//                       placeholder="Variation Label"
//                       className="flex-1"
//                       autoFocus
//                     />
//                   )}
//                 </form.AppField>

//                 <Button
//                   variant="ghost"
//                   size="icon"
//                   onClick={() => {
//                     field.removeValue(i);
//                     setSelectedVariationIndex(null);
//                   }}
//                 >
//                   <X className="size-4" />
//                 </Button>
//               </div>
//               {/*
//               <Text variant="sm" className="mb-1 text-muted-foreground">
//                 Variation details
//               </Text>
//               <textarea
//                 className="mb-2 min-h-20 w-full rounded-md border p-2"
//                 value={selectedVariation.recipe.description}
//                 onChange={(event) =>
//                   updateSelectedVariation((variation) => ({
//                     ...variation,
//                     recipe: {
//                       ...variation.recipe,
//                       description: event.target.value,
//                     },
//                   }))
//                 }
//                 placeholder="Variation description"
//               /> */}

//               <Text variant="sm" className="mb-1 text-muted-foreground">
//                 Ingredients
//               </Text>
//               <form.AppField name={`curatedVariations[${i}].ingredients`} mode="array">
//                 {(field) => (
//                   <div className="flex flex-col gap-2">
//                     <FoodSearchCombobox
//                       onSelect={(item) => RecipeForm.addFoodItemToIngredients(field, item)}
//                     />

//                     <div className="max-h-64 overflow-y-auto">
//                       <div className="flex flex-col gap-2">
//                         {field.state.value.map((ingredient, i) => (
//                           <FoodItemInputView
//                             item={ingredient}
//                             onEditItemAmount={(amount) =>
//                               field.replaceValue(i, { ...ingredient, amount })
//                             }
//                             onEditItemServing={(serving) =>
//                               field.replaceValue(i, {
//                                 ...ingredient,
//                                 serving,
//                                 amount: 1,
//                               })
//                             }
//                             onRemoveItem={() => field.removeValue(i)}
//                           />
//                         ))}
//                         {/* TODO: Show badges here? */}
//                       </div>
//                     </div>

//                     {field.state.meta.errors.length > 0 && (
//                       <Text variant="sm" className="text-destructive">
//                         {field.state.meta.errors.map((error) => error?.message).join(", ")}
//                       </Text>
//                     )}
//                   </div>
//                 )}
//               </form.AppField>

//               <Text variant="sm" className="mb-1 mt-3 text-muted-foreground">
//                 Instructions
//               </Text>
//               <form.AppField name={`curatedVariations[${i}].steps`} mode="array">
//                 {(field) => (
//                   <>
//                     {field.state.value.map((_step, stepIndex) => (
//                       <div key={stepIndex} className="flex flex-row gap-2">
//                         <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
//                           <Text variant="sm" className="text-primary-foreground">
//                             {stepIndex + 1}
//                           </Text>
//                         </div>
//                         <form.AppField name={`curatedVariations[${i}].steps[${stepIndex}].text`}>
//                           {(subField) => (
//                             <subField.Textarea
//                               className="flex-1"
//                               placeholder={`Step ${stepIndex + 1} instructions`}
//                             />
//                           )}
//                         </form.AppField>
//                         {field.state.value.length > 1 && (
//                           <Button
//                             variant="ghost"
//                             size="icon"
//                             onClick={() => field.removeValue(stepIndex)}
//                           >
//                             <X className="size-4" />
//                           </Button>
//                         )}
//                       </div>
//                     ))}

//                     <Button variant="ghost" onClick={() => RecipeForm.addStep(field)}>
//                       <Text variant="sm">Add Step</Text>
//                     </Button>
//                   </>
//                 )}
//               </form.AppField>
//             </div>
//           ))}
//         </div>
//       )}
//     </form.AppField>
//   );
// }
